import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CloudUpload, File, FileArchive, FileAudio, FileCode, FileJson, FileText, FileVideo, Folder, FolderOpen, Image as ImageIcon, Info } from 'lucide-react';
import { Spinner } from '@/components/app/misc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
/** Caracteres proibidos em nomes (mesma regra do backend). */
export const INVALID_NAME = /[\/\\:*?"<>|]/;
export function formatSize(bytes) {
    if (!bytes)
        return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}
export function formatDate(ts) {
    if (!ts)
        return '-';
    const d = new Date(ts * 1000);
    const p = (n) => ('0' + n).slice(-2);
    return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
export function extOf(name) {
    return name.includes('.') ? name.split('.').pop().toLowerCase() : '';
}
/** Normaliza a resposta de cpanel.files.list (mesmas regras da tela antiga). */
export function normalizeFiles(files) {
    const out = [];
    for (const f of files || []) {
        let name = f.file || f.name || f.fullpath || '';
        if (name === '.' || name === '..')
            continue;
        if (!f.file && !f.name && f.fullpath)
            name = String(f.fullpath).split('/').pop() || '';
        let type = 'file';
        if (f.type === 'dir' || f.type === 'directory' || f.mimetype === 'httpd/unix-directory' || f.isdir === 1 || f.isdir === '1')
            type = 'dir';
        out.push({
            name,
            type,
            size: parseInt(f.size || f.rawsize || 0, 10) || 0,
            mtime: parseInt(f.mtime || f.modifiedtime || f.ctime || 0, 10) || 0,
            mimetype: f.mimetype || f.mime || '',
            isProtected: !!f.is_protected,
        });
    }
    return out.sort((a, b) => {
        const ad = a.type === 'dir' ? 0 : 1;
        const bd = b.type === 'dir' ? 0 : 1;
        if (ad !== bd)
            return ad - bd;
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
}
export function FileIcon({ item }) {
    const base = 'flex h-8 w-8 shrink-0 items-center justify-center rounded-md [&_svg]:size-4';
    if (item.type === 'dir') {
        return (_jsx("span", { className: cn(base, 'bg-primary/10 text-primary'), children: _jsx(Folder, {}) }));
    }
    const ext = extOf(item.name);
    let Icon = File;
    let tone = 'bg-muted text-muted-foreground';
    if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'ico', 'bmp'].includes(ext)) {
        Icon = ImageIcon;
        tone = 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400';
    }
    else if (['json', 'xml'].includes(ext)) {
        Icon = FileJson;
        tone = 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
    }
    else if (['html', 'htm', 'css', 'js', 'php', 'py', 'rb', 'ts', 'jsx', 'vue', 'sql'].includes(ext)) {
        Icon = FileCode;
        tone = 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300';
    }
    else if (['zip', 'rar', 'gz', 'tar', '7z'].includes(ext))
        Icon = FileArchive;
    else if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext))
        Icon = FileVideo;
    else if (['mp3', 'wav', 'ogg', 'flac'].includes(ext))
        Icon = FileAudio;
    else if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(ext))
        Icon = FileText;
    return (_jsx("span", { className: cn(base, tone), children: _jsx(Icon, {}) }));
}
// ─── Validação no navegador (feedback rápido; o backend valida de novo) ───
const BLOCKED = ['zip', 'rar', 'tar', 'gz', 'bz2', '7z', 'xz', 'tgz', 'cab', 'iso', 'dmg', 'mp4', 'avi', 'mov', 'mkv', 'wmv', 'flv', 'webm', 'mpeg', 'mpg', 'm4v', '3gp', 'ogv', 'exe', 'bat', 'cmd', 'sh', 'cgi', 'pl', 'py', 'rb', 'jar', 'msi', 'dll', 'so', 'bin'];
const WP_FILES = ['wp-config.php', 'wp-login.php', 'wp-settings.php', 'wp-cron.php', 'wp-blog-header.php', 'wp-load.php'];
const MAX_TOTAL = 52428800; // 50MB
export function validateFilesClient(files, relativePaths) {
    const errors = [];
    let total = 0;
    for (let i = 0; i < files.length; i++) {
        const name = files[i].name;
        const path = relativePaths ? relativePaths[i] : name;
        const ext = name.split('.').pop().toLowerCase();
        total += files[i].size;
        if (BLOCKED.includes(ext))
            errors.push(`"${path}" - Tipo não permitido (.${ext})`);
        if (WP_FILES.includes(name.toLowerCase()))
            errors.push(`"${path}" - Arquivo WordPress não permitido`);
        if (relativePaths) {
            for (const part of path.toLowerCase().split('/')) {
                if (part === 'wp-admin' || part === 'wp-includes' || part === 'wp-content') {
                    errors.push(`Pasta "${part}" indica WordPress - não permitido`);
                    return errors;
                }
            }
        }
    }
    if (total > MAX_TOTAL)
        errors.push(`Tamanho total (${(total / 1048576).toFixed(1)}MB) excede o limite de 50MB`);
    return errors;
}
/** Janela "Envio bloqueado" com a lista de motivos (antes era um SweetAlert). */
export function UploadErrorsDialog({ value, onClose }) {
    return (_jsx(Dialog, { open: !!value, onOpenChange: (open) => !open && onClose(), children: _jsxs(DialogContent, { className: "max-h-[90vh] max-w-md overflow-y-auto", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "flex items-center gap-2", children: [_jsx(AlertTriangle, { className: "h-5 w-5 text-destructive" }), "Envio bloqueado"] }), _jsx(DialogDescription, { children: value === null || value === void 0 ? void 0 : value.message })] }), _jsx("ul", { className: "list-disc space-y-1 pl-5 text-[13px]", children: value === null || value === void 0 ? void 0 : value.errors.map((error, i) => (_jsx("li", { className: "break-words", children: error }, i))) }), _jsx(DialogFooter, { children: _jsx(Button, { variant: "destructive", onClick: onClose, children: "Entendi" }) })] }) }));
}
/**
 * Janela de envio de arquivos ou de uma pasta inteira (multipart via axios,
 * com barra de progresso como na tela antiga).
 */
export function UploadDialog({ mode, open, onClose, url, currentDir, onDone, onErrors, }) {
    const inputRef = React.useRef(null);
    const [files, setFiles] = React.useState([]);
    const [paths, setPaths] = React.useState([]);
    const [valid, setValid] = React.useState(false);
    const [dragOver, setDragOver] = React.useState(false);
    const [progress, setProgress] = React.useState(null);
    const [sending, setSending] = React.useState(false);
    const isFolder = mode === 'folder';
    React.useEffect(() => {
        if (open) {
            setFiles([]);
            setPaths([]);
            setValid(false);
            setProgress(null);
            setSending(false);
            if (inputRef.current)
                inputRef.current.value = '';
        }
    }, [open]);
    const setInputRef = React.useCallback((node) => {
        inputRef.current = node;
        if (node && isFolder) {
            node.setAttribute('webkitdirectory', '');
            node.setAttribute('directory', '');
        }
    }, [isFolder]);
    const pick = (list) => {
        const selected = list ? Array.from(list) : [];
        const rel = isFolder ? selected.map((f) => f.webkitRelativePath || f.name) : [];
        setFiles(selected);
        setPaths(rel);
        const errors = validateFilesClient(selected, isFolder ? rel : null);
        if (errors.length) {
            onErrors({ message: isFolder ? 'Arquivos não permitidos na pasta:' : 'Arquivos não permitidos:', errors });
            setValid(false);
            return;
        }
        setValid(selected.length > 0);
    };
    const submit = () => {
        if (!files.length || !valid)
            return;
        const form = new FormData();
        form.append('current_dir', currentDir);
        if (isFolder) {
            form.append('relative_paths', JSON.stringify(paths));
            files.forEach((f) => form.append('folder_files[]', f));
        }
        else {
            files.forEach((f) => form.append('files[]', f));
        }
        setSending(true);
        setProgress(0);
        axios
            .post(url, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (e) => {
                if (e.total)
                    setProgress(Math.round((e.loaded / e.total) * 100));
            },
        })
            .then(({ data }) => {
            if (data === null || data === void 0 ? void 0 : data.success) {
                toast.success(data.message);
                onClose();
                onDone();
            }
            else {
                showServerErrors(data);
            }
        })
            .catch((error) => {
            var _a;
            const data = (_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.data;
            if (data && typeof data === 'object')
                showServerErrors(data);
            else
                toast.error(isFolder ? 'Erro ao enviar pasta.' : 'Erro ao enviar arquivos.');
        })
            .finally(() => setSending(false));
    };
    const showServerErrors = (data) => {
        setProgress(null);
        const list = Array.isArray(data === null || data === void 0 ? void 0 : data.errors) ? data.errors : [];
        if (!list.length && (data === null || data === void 0 ? void 0 : data.errors) && typeof data.errors === 'object') {
            // erros de validação do Laravel ({ campo: [mensagens] })
            Object.values(data.errors).forEach((v) => list.push(...(Array.isArray(v) ? v : [String(v)])));
        }
        if (list.length)
            onErrors({ message: (data === null || data === void 0 ? void 0 : data.message) || 'Envio bloqueado', errors: list });
        else
            toast.error((data === null || data === void 0 ? void 0 : data.message) || 'Envio bloqueado');
    };
    const shown = files.slice(0, 15);
    return (_jsx(Dialog, { open: open, onOpenChange: (value) => !value && !sending && onClose(), children: _jsxs(DialogContent, { className: "max-h-[90vh] max-w-lg overflow-y-auto", children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: isFolder ? 'Subir pasta' : 'Subir arquivos' }), _jsxs(DialogDescription, { className: "flex items-start gap-1.5 text-orange-700 dark:text-orange-300", children: [_jsx(Info, { className: "mt-0.5 h-3.5 w-3.5 shrink-0" }), isFolder ? 'Arquivos com o mesmo nome serão sobrescritos. A estrutura de subpastas será mantida.' : 'Arquivos com o mesmo nome serão sobrescritos automaticamente.'] })] }), _jsxs("button", { type: "button", onClick: () => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, onDragOver: (e) => {
                        if (isFolder)
                            return;
                        e.preventDefault();
                        setDragOver(true);
                    }, onDragLeave: (e) => {
                        e.preventDefault();
                        setDragOver(false);
                    }, onDrop: (e) => {
                        if (isFolder)
                            return;
                        e.preventDefault();
                        setDragOver(false);
                        pick(e.dataTransfer.files);
                    }, disabled: sending, className: cn('flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors hover:border-primary/60 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring', dragOver && 'border-primary bg-primary/5'), children: [isFolder ? _jsx(FolderOpen, { className: "h-8 w-8 text-primary" }) : _jsx(CloudUpload, { className: "h-8 w-8 text-primary" }), _jsx("span", { className: "text-sm font-medium", children: isFolder ? 'Clique para selecionar uma pasta' : 'Arraste arquivos aqui ou clique para selecionar' }), !isFolder ? _jsx("span", { className: "text-xs text-muted-foreground", children: "Tamanho m\u00E1ximo: 50MB por arquivo" }) : null] }), _jsx("input", { ref: setInputRef, type: "file", multiple: true, className: "hidden", "aria-hidden": "true", tabIndex: -1, onChange: (e) => pick(e.target.files) }), files.length ? (_jsxs("div", { className: "max-h-48 overflow-y-auto rounded-md border bg-muted/40 px-3 py-2 text-[13px]", children: [shown.map((f, i) => (_jsxs("div", { className: "flex items-center gap-2 py-0.5", children: [_jsx(File, { className: "h-3.5 w-3.5 shrink-0 text-muted-foreground" }), _jsx("span", { className: "truncate", children: isFolder ? paths[i] : f.name }), _jsxs("span", { className: "shrink-0 text-xs text-muted-foreground", children: ["(", formatSize(f.size), ")"] })] }, i))), files.length > 15 ? _jsxs("div", { className: "py-1 text-center text-xs text-muted-foreground", children: ["... e mais ", files.length - 15, " arquivo(s)"] }) : null] })) : null, progress !== null ? (_jsxs("div", { className: "grid gap-1.5", "aria-live": "polite", children: [_jsx(Progress, { value: progress, "aria-label": "Progresso do envio" }), _jsxs("span", { className: "text-xs text-muted-foreground", children: ["Enviando... ", progress, "%"] })] })) : null, _jsxs(DialogFooter, { className: "gap-2 sm:gap-0", children: [_jsx(Button, { variant: "outline", onClick: onClose, disabled: sending, children: "Cancelar" }), _jsxs(Button, { onClick: submit, disabled: !valid || sending, children: [sending ? _jsx(Spinner, {}) : null, isFolder ? 'Enviar pasta' : 'Enviar arquivos'] })] })] }) }));
}
